import React, { Fragment, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Table from './Table';
import FormModal from './FormModal';
import DeleteModal from './DeleteModal';
import UploadModal from './UploadModal';
import './AdminPromotionalItems.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as actions from './AdminPromotionalItems.actions';
import * as selectors from './AdminPromotionalItems.selectors';
import { stringifyEqualityCheck } from './../../utils';
import * as dialog from './../common/dialog';
import * as loginSelectors from './../Login/Login.selectors';
import { showNotification } from './../../utils';

const AdminPromotionalItems = (props) => {
    /* State to props */
    const promotionalItems = useSelector(selectors.getPromotionalItems, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const saving = useSelector(selectors.getSaving);
    const isDeleting = useSelector(selectors.getIsDeleting);
    const idModal = useSelector(selectors.getIdModal);
    const pagination = useSelector(selectors.getPagination, stringifyEqualityCheck);
    const imageFile = useSelector(selectors.getImageFile, stringifyEqualityCheck);
    const promotionalItem = useSelector(selectors.getPromotionalItem, stringifyEqualityCheck);
    const departments = useSelector(selectors.getDepartments, stringifyEqualityCheck);
    const sort = useSelector(selectors.getSort, stringifyEqualityCheck);
    const search = useSelector(selectors.getSearch);
    const searchType = useSelector(selectors.getSearchType);
    const isUploading = useSelector(selectors.getIsUploading);
    const uploadFile = useSelector(selectors.getUploadFile, stringifyEqualityCheck);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchPromotionalItems = useCallback((type) => dispatch(actions.fetchPromotionalItems(type)), [dispatch]);
    const setIdModal = useCallback((id) => dispatch(actions.setIdModal(id)), [dispatch]);
    const setActivePage = useCallback((value) => dispatch(actions.setActivePage(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const setPromotionalItem = useCallback((id) => dispatch(actions.setPromotionalItem(id)), [dispatch]);
    const setPromotionalItemField = useCallback((field, value) => dispatch(actions.setPromotionalItemField(field, value)), [dispatch]);
    const setImageFile = useCallback((file) => dispatch(actions.setImageFile(file)), [dispatch]);
    const resetPromotionalItem = useCallback(() => dispatch(actions.resetPromotionalItem()), [dispatch]);
    const fetchDepartments = useCallback(() => dispatch(actions.fetchDepartments()), [dispatch]);
    const savePromotionalItem = useCallback(() => dispatch(actions.savePromotionalItem()), [dispatch]);
    const deletePromotionalItem = useCallback(() => dispatch(actions.deletePromotionalItem()), [dispatch]);
    const setSort = useCallback((column, type) => dispatch(actions.setSort(column, type)), [dispatch]);
    const setSearch = useCallback((value) => dispatch(actions.setSearch(value)), [dispatch]);
    const setSearchType = useCallback((value) => dispatch(actions.setSearchType(value)), [dispatch]);
    const setUploadFile = useCallback((file) => dispatch(actions.setUploadFile(file)), [dispatch]);
    const uploadExcel = useCallback(() => dispatch(actions.uploadExcel()), [dispatch]);
    const setPrice = useCallback((index, price) => dispatch(actions.setPrice(index, price)), [dispatch]);
    const setEnabled = useCallback((index, enabled) => dispatch(actions.setEnabled(index, enabled)), [dispatch]);
    const showFormModal = useCallback(() => dispatch(dialog.actions.showDialog('form-modal')), [dispatch]);
    const hideFormModal = useCallback(() => dispatch(dialog.actions.hideDialog('form-modal')), [dispatch]);
    const showDeleteModal = useCallback(() => dispatch(dialog.actions.showDialog('delete-modal')), [dispatch]);
    const hideDeleteModal = useCallback(() => dispatch(dialog.actions.hideDialog('delete-modal')), [dispatch]);
    // const showUploadModal = useCallback(() => dispatch(dialog.actions.showDialog('upload-modal')), [dispatch]);
    const hideUploadModal = useCallback(() => dispatch(dialog.actions.hideDialog('upload-modal')), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        fetchPromotionalItems();
        fetchDepartments();
        return () => {
            hideFormModal();
            resetState();
        };
    }, [fetchPromotionalItems, resetState, fetchDepartments, hideFormModal]);

    let permissions = [];
    if (user && user.uth_permissions != null) {
        permissions = JSON.parse(user.uth_permissions);
    }

    const handleSearch = () => {
        if (props.search === '') {
            return showNotification('Enter a value', 'Enter a text to search', 'info');
        }

        setActivePage(1);
        fetchPromotionalItems();
    };

    const keyPressHandler = (event) => {
        if (event.key === 'Enter') {
            if (props.search === '') {
                return showNotification('Enter a value', 'Enter a text to search', 'info');
            }

            setActivePage(1);
            fetchPromotionalItems();
        }
    };

    let createButton = null;
    if (permissions.includes('promotional-create')) {
        createButton = <div className="create-button" onClick={showFormModal}><i className="fas fa-folder-plus" /> Add Item</div>;
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-file-alt"></i> Promotional Items</Fragment>}
            history={props.history}
        >
            <Helmet><title>Promotional Items</title></Helmet>
            <div className="admin-promotional-items-container">
                <h2>Promotional Items</h2>
                {createButton}
                <div className="search-container">
                    <div class="search-field">
                        <input
                            type="text"
                            placeholder="Search here..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyPress={keyPressHandler}
                        />
                        <select
                            value={searchType}
                            onChange={(event) => setSearchType(event.target.value)}
                        >
                            <option value="id">By ID</option>
                            <option value="sku">By SKU</option>
                            <option value="name">By Name</option>
                            {/* <option value="department">By Department</option> */}
                        </select>
                        <div className="button" onClick={handleSearch}>Search</div>
                    </div>
                </div>
                <Table
                    promotionalItems={promotionalItems}
                    fetching={fetching}
                    user={user}
                    pagination={pagination}
                    sort={sort}
                    showFormModal={showFormModal}
                    setIdModal={setIdModal}
                    setActivePage={setActivePage}
                    setPromotionalItem={setPromotionalItem}
                    showDeleteModal={showDeleteModal}
                    setSort={setSort}
                />
            </div>
            <FormModal
                promotionalItem={promotionalItem}
                saving={saving}
                idModal={idModal}
                imageFile={imageFile}
                departments={departments}
                hideFormModal={hideFormModal}
                setIdModal={setIdModal}
                setPromotionalItemField={setPromotionalItemField}
                setPromotionalItem={setPromotionalItem}
                setImageFile={setImageFile}
                resetPromotionalItem={resetPromotionalItem}
                savePromotionalItem={savePromotionalItem}
                setPrice={setPrice}
                setEnabled={setEnabled}
            />
            <DeleteModal
                idModal={idModal}
                isDeleting={isDeleting}
                deletePromotionalItem={deletePromotionalItem}
                hideDeleteModal={hideDeleteModal}
                setIdModal={setIdModal}
            />
            <UploadModal
                isUploading={isUploading}
                uploadFile={uploadFile}
                setUploadFile={setUploadFile}
                uploadExcel={uploadExcel}
                hideUploadModal={hideUploadModal}
            />
        </Layout>
    );
};

export default AdminPromotionalItems;
